// =============================================================================
// MeuDriver — Batch R4: StudentSupport
// Lista de tickets de suporte do aluno + criação + thread de mensagens
// Rota: /aluno/suporte
// =============================================================================

import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  LifeBuoy,
  Plus,
  Loader2,
  ChevronRight,
  Send,
  MessageCircle,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
} from 'lucide-react'
import { formatDistanceToNow, parseISO } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { toast } from 'sonner'
import {
  useMyTickets,
  useCreateTicket,
  useTicketMessages,
  useSendTicketMessage,
} from '@/hooks/useSupportTickets'
import type { SupportTicketRow, SupportTicketStatus } from '@/types/database'
import { useAuth } from '@/hooks/useAuth'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

// ---------------------------------------------------------------------------
// Helpers de status
// ---------------------------------------------------------------------------

function StatusBadge({ status }: { status: SupportTicketStatus }) {
  const map: Record<SupportTicketStatus, { label: string; className: string }> = {
    open:        { label: 'Aberto',       className: 'bg-blue-100 text-blue-700' },
    in_progress: { label: 'Em análise',   className: 'bg-yellow-100 text-yellow-700' },
    resolved:    { label: 'Resolvido',    className: 'bg-green-100 text-green-700' },
    closed:      { label: 'Fechado',      className: 'bg-gray-100 text-gray-500' },
  }
  const { label, className } = map[status]
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${className}`}>
      {label}
    </span>
  )
}

function StatusIcon({ status }: { status: SupportTicketStatus }) {
  switch (status) {
    case 'open':        return <Clock        className="h-4 w-4 text-blue-500" />
    case 'in_progress': return <AlertCircle  className="h-4 w-4 text-yellow-500" />
    case 'resolved':    return <CheckCircle2 className="h-4 w-4 text-green-500" />
    case 'closed':      return <XCircle      className="h-4 w-4 text-gray-400" />
  }
}

// ---------------------------------------------------------------------------
// Formulário de novo ticket
// ---------------------------------------------------------------------------

const ticketSchema = z.object({
  subject:     z.string().min(5, 'Assunto muito curto').max(200, 'Assunto muito longo'),
  description: z.string().min(20, 'Descreva melhor o problema').max(2000, 'Limite de 2000 caracteres'),
})

type TicketFormData = z.infer<typeof ticketSchema>

interface NewTicketModalProps {
  open: boolean
  onClose: () => void
}

function NewTicketModal({ open, onClose }: NewTicketModalProps) {
  const create = useCreateTicket()
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<TicketFormData>({ resolver: zodResolver(ticketSchema) })

  const descLen = (watch('description') ?? '').length

  async function onSubmit(data: TicketFormData) {
    try {
      await create.mutateAsync({ subject: data.subject, description: data.description })
      toast.success('Ticket aberto com sucesso!')
      reset()
      onClose()
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Erro ao abrir ticket.'
      toast.error(msg)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { reset(); onClose() } }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <LifeBuoy className="h-4 w-4" />
            Abrir ticket de suporte
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 mt-2">
          <div className="space-y-1.5">
            <Label htmlFor="subject">Assunto *</Label>
            <Input
              id="subject"
              placeholder="Descreva brevemente o problema"
              {...register('subject')}
              className={errors.subject ? 'border-red-400' : ''}
            />
            {errors.subject && <p className="text-xs text-red-600">{errors.subject.message}</p>}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="description">
              Descrição *
              <span className="ml-1 text-xs text-muted-foreground font-normal">({descLen}/2000)</span>
            </Label>
            <Textarea
              id="description"
              placeholder="Detalhe o problema, o que você esperava e o que aconteceu..."
              rows={5}
              {...register('description')}
              className={errors.description ? 'border-red-400' : ''}
              maxLength={2000}
            />
            {errors.description && <p className="text-xs text-red-600">{errors.description.message}</p>}
          </div>

          <div className="flex gap-3 justify-end pt-1">
            <Button type="button" variant="outline" onClick={() => { reset(); onClose() }}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting} className="gap-2">
              {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
              Abrir ticket
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}

// ---------------------------------------------------------------------------
// Thread de mensagens
// ---------------------------------------------------------------------------

interface TicketThreadProps {
  ticket: SupportTicketRow
  onClose: () => void
}

function TicketThread({ ticket, onClose }: TicketThreadProps) {
  const { user } = useAuth()
  const { data: messages = [], isLoading } = useTicketMessages(ticket.id)
  const send = useSendTicketMessage()
  const [body, setBody] = useState('')

  async function handleSend() {
    if (!body.trim()) return
    try {
      await send.mutateAsync({ ticketId: ticket.id, body: body.trim() })
      setBody('')
    } catch {
      toast.error('Erro ao enviar mensagem.')
    }
  }

  const canReply = ticket.status !== 'closed' && ticket.status !== 'resolved'

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose() }}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <div className="flex items-start justify-between gap-2 pr-6">
            <DialogTitle className="text-base leading-snug">{ticket.subject}</DialogTitle>
            <StatusBadge status={ticket.status} />
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Ticket #{ticket.id.slice(0, 8)} · aberto{' '}
            {formatDistanceToNow(parseISO(ticket.created_at), { addSuffix: true, locale: ptBR })}
          </p>
        </DialogHeader>

        <Separator />

        {/* Descrição original */}
        <div className="bg-gray-50 rounded-xl px-4 py-3 text-sm text-gray-700">
          <p className="font-medium text-xs text-muted-foreground mb-1">Descrição original:</p>
          <p className="whitespace-pre-line">{ticket.description}</p>
        </div>

        {/* Mensagens */}
        <ScrollArea className="flex-1 min-h-0 max-h-64">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : messages.length === 0 ? (
            <div className="flex flex-col items-center py-8 gap-2 text-center text-muted-foreground">
              <MessageCircle className="h-8 w-8 opacity-20" />
              <p className="text-sm">Nenhuma mensagem ainda.</p>
            </div>
          ) : (
            <div className="space-y-3 py-2 px-1">
              {messages.map((m) => {
                const isMe = m.sender_id === user?.id
                return (
                  <div
                    key={m.id}
                    className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm ${
                        isMe
                          ? 'bg-brand-600 text-white rounded-br-md'
                          : m.is_staff_reply
                            ? 'bg-orange-50 border border-orange-200 text-gray-800 rounded-bl-md'
                            : 'bg-gray-100 text-gray-800 rounded-bl-md'
                      }`}
                    >
                      {m.is_staff_reply && !isMe && (
                        <p className="text-[10px] font-bold text-orange-600 mb-0.5 uppercase tracking-wide">
                          Suporte
                        </p>
                      )}
                      <p className="whitespace-pre-line leading-snug">{m.body}</p>
                      <p className={`text-[10px] mt-1 ${isMe ? 'text-white/60' : 'text-muted-foreground'}`}>
                        {formatDistanceToNow(parseISO(m.created_at), { addSuffix: true, locale: ptBR })}
                      </p>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </ScrollArea>

        {/* Input de resposta */}
        {canReply && (
          <>
            <Separator />
            <div className="flex gap-2">
              <Textarea
                placeholder="Escreva uma mensagem..."
                rows={2}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                maxLength={2000}
                className="resize-none flex-1"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) handleSend()
                }}
                data-testid="reply-input"
              />
              <Button
                onClick={handleSend}
                disabled={!body.trim() || send.isPending}
                size="icon"
                className="h-full px-3 self-end"
                data-testid="send-reply-btn"
              >
                {send.isPending
                  ? <Loader2 className="h-4 w-4 animate-spin" />
                  : <Send className="h-4 w-4" />
                }
              </Button>
            </div>
          </>
        )}

        {!canReply && (
          <p className="text-xs text-center text-muted-foreground">
            Este ticket está {ticket.status === 'closed' ? 'fechado' : 'resolvido'} e não aceita mais mensagens.
          </p>
        )}
      </DialogContent>
    </Dialog>
  )
}

// ---------------------------------------------------------------------------
// Página principal
// ---------------------------------------------------------------------------

export default function StudentSupport() {
  const [newTicketOpen, setNewTicketOpen] = useState(false)
  const [selectedTicket, setSelectedTicket] = useState<SupportTicketRow | null>(null)

  const { data: tickets = [], isLoading } = useMyTickets()

  return (
    <div className="max-w-2xl mx-auto space-y-6">

      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <LifeBuoy className="h-6 w-6 text-brand-600" />
            Central de Suporte
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Abra tickets para dúvidas ou problemas na plataforma.
          </p>
        </div>
        <Button
          onClick={() => setNewTicketOpen(true)}
          className="gap-2 shrink-0"
          data-testid="new-ticket-btn"
        >
          <Plus className="h-4 w-4" />
          Novo ticket
        </Button>
      </div>

      {/* Lista */}
      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => <Skeleton key={i} className="h-20 rounded-xl" />)}
        </div>
      ) : tickets.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 gap-3 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-gray-100">
              <LifeBuoy className="h-7 w-7 text-gray-300" />
            </div>
            <p className="font-medium text-gray-700">Nenhum ticket aberto</p>
            <p className="text-sm text-muted-foreground">
              Se tiver alguma dúvida ou problema, abra um ticket e nossa equipe responderá em breve.
            </p>
            <Button onClick={() => setNewTicketOpen(true)} className="mt-2 gap-2">
              <Plus className="h-4 w-4" />
              Abrir meu primeiro ticket
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {tickets.map((t) => (
            <button
              key={t.id}
              data-testid="ticket-row"
              className="w-full text-left rounded-xl border border-gray-200 bg-white p-4 hover:border-brand-300 hover:bg-brand-50/30 transition-all"
              onClick={() => setSelectedTicket(t)}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 min-w-0">
                  <div className="mt-0.5 shrink-0">
                    <StatusIcon status={t.status} />
                  </div>
                  <div className="min-w-0">
                    <p className="font-semibold text-sm text-gray-900 truncate">{t.subject}</p>
                    <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
                      {t.description}
                    </p>
                    <p className="text-[11px] text-muted-foreground/70 mt-1">
                      {formatDistanceToNow(parseISO(t.created_at), { addSuffix: true, locale: ptBR })}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <StatusBadge status={t.status} />
                  <ChevronRight className="h-4 w-4 text-gray-400" />
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Modals */}
      <NewTicketModal open={newTicketOpen} onClose={() => setNewTicketOpen(false)} />
      {selectedTicket && (
        <TicketThread ticket={selectedTicket} onClose={() => setSelectedTicket(null)} />
      )}
    </div>
  )
}
